import { create } from 'zustand';
import type { AgentInfo } from '@clawlet/gateway-client';
import { gatewayRef } from '../utils/gatewayRef';
import { getGatewayUrlForCurrentServer } from '../utils/serverOrigin';

// ============================================================
// Subagent Status UI Types
// ============================================================

export interface ChatTab {
  id: string;
  sessionKey: string;
  hasUnread: boolean;
}

export interface SubagentInfo {
  sessionKey: string;
  parentSessionKey?: string; // originating chat tab session key (set on first upsert)
  label: string;
  rawLabel?: string | null;
  status: 'running' | 'completed' | 'error';
  streamingText: string;
  startTime: number;         // Date.now() timestamp
  lastActivityTime: number;
  hasReceivedLiveEvent: boolean;
  completedAt: number | null;
  acknowledged: boolean;     // user has opened popup to view result
  fadingOut: boolean;         // currently in fade-out animation before removal
  autoOpenOnStreaming: boolean; // /btw responses should auto-open once live text starts
}

/** Check if a session key belongs to a subagent */
export type UserRole = 'builder' | 'member';

export function isSubagentSessionKey(key: string): boolean {
  return key.includes(':subagent:');
}

/** Fallback label when no better subagent metadata is available */
function fallbackSubagentLabel(sessionKey: string): string {
  // "agent:main:subagent:f7f8b72d-46c8-..." → "subagent-f7f8b72d"
  const parts = sessionKey.split(':');
  const lastPart = parts[parts.length - 1] ?? '';
  const shortId = lastPart.slice(0, 8);
  return `subagent-${shortId}`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatAgentDisplayName(agentId: string | null | undefined): string | null {
  const normalized = agentId?.trim();
  if (!normalized) return null;
  if (normalized === 'main') return 'Flint';

  return normalized
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function extractSubagentTaskLabel(sessionKey: string, rawLabel: string | null | undefined): string | null {
  const trimmed = rawLabel?.trim();
  if (!trimmed) return null;

  const fallbackLabel = fallbackSubagentLabel(sessionKey);
  if (trimmed === fallbackLabel) return null;

  const agentId = isSubagentSessionKey(sessionKey) ? agentIdFromSessionKey(sessionKey) : '';
  if (!agentId) return trimmed;

  const stripped = trimmed.replace(new RegExp(`^${escapeRegExp(agentId)}(?:[\\s—–:_-]+)?`, 'i'), '').trim();
  return stripped || null;
}

function sessionKeyFromSession(session: any): string | null {
  if (typeof session?.sessionKey === 'string') return session.sessionKey;
  if (typeof session?.key === 'string') return session.key;
  if (typeof session?.id === 'string') return session.id;
  return null;
}

function findSessionLabel(sessions: any[], sessionKey: string): string | null {
  const session = sessions.find((candidate) => sessionKeyFromSession(candidate) === sessionKey);
  const label = session?.label ?? session?.title ?? null;
  return typeof label === 'string' && label.trim() ? label.trim() : null;
}

/** Derive a human-friendly label from a subagent session key and optional raw label */
export function deriveSubagentLabel(sessionKey: string, rawLabel?: string | null): string {
  const agentName =
    isSubagentSessionKey(sessionKey) && sessionKey.startsWith('agent:')
      ? formatAgentDisplayName(agentIdFromSessionKey(sessionKey))
      : null;
  const taskLabel = extractSubagentTaskLabel(sessionKey, rawLabel);

  if (agentName && taskLabel) {
    return `${agentName} — ${taskLabel}`;
  }

  if (agentName) {
    return agentName;
  }

  return fallbackSubagentLabel(sessionKey);
}

function isDefaultDerivedSubagentLabel(sessionKey: string, rawLabel?: string | null, label?: string): boolean {
  if (!isSubagentSessionKey(sessionKey) || !sessionKey.startsWith('agent:')) {
    return false;
  }

  const agentName = formatAgentDisplayName(agentIdFromSessionKey(sessionKey));
  if (!agentName) {
    return false;
  }

  const derivedLabel = label ?? deriveSubagentLabel(sessionKey, rawLabel);
  return derivedLabel === agentName && !extractSubagentTaskLabel(sessionKey, rawLabel);
}

const SUBAGENT_LABEL_REFRESH_DEBOUNCE_MS = 1200;
let subagentLabelRefreshTimer: ReturnType<typeof setTimeout> | null = null;
const attemptedSubagentLabelRefreshes = new Set<string>();

function clearResolvedSubagentLabelRefreshes(subagentRuns: Record<string, SubagentInfo>): void {
  for (const [sessionKey, info] of Object.entries(subagentRuns)) {
    if (!isDefaultDerivedSubagentLabel(sessionKey, info.rawLabel, info.label)) {
      attemptedSubagentLabelRefreshes.delete(sessionKey);
    }
  }
}

function scheduleSubagentLabelRefresh(sessionKey: string): void {
  if (attemptedSubagentLabelRefreshes.has(sessionKey)) {
    return;
  }

  attemptedSubagentLabelRefreshes.add(sessionKey);

  if (subagentLabelRefreshTimer) {
    return;
  }

  subagentLabelRefreshTimer = setTimeout(() => {
    subagentLabelRefreshTimer = null;

    const client = gatewayRef.current;
    if (!client?.connected) {
      return;
    }

    void client
      .listSessions()
      .then((sessions) => {
        useAppStore.getState().setSessions(sessions);
      })
      .catch((error) => {
        console.debug('[appStore] Failed to refresh subagent labels from sessions.list:', error);
      });
  }, SUBAGENT_LABEL_REFRESH_DEBOUNCE_MS);
}

/** Maximum streaming text size (10KB) to prevent memory issues */
const MAX_SUBAGENT_STREAMING_TEXT = 10 * 1024;
const SUBAGENT_TRIM_PREFIX = '…\n\n';
const SUBAGENT_TRIM_LINE_LOOKAHEAD = 400;

function trimSubagentStreamingText(text: string): string {
  if (text.length <= MAX_SUBAGENT_STREAMING_TEXT) {
    return text;
  }

  const targetStart = text.length - MAX_SUBAGENT_STREAMING_TEXT;
  const nextNewline = text.indexOf('\n', targetStart);
  const sliceStart =
    nextNewline !== -1 && nextNewline - targetStart <= SUBAGENT_TRIM_LINE_LOOKAHEAD
      ? nextNewline + 1
      : targetStart;

  const trimmed = text.slice(sliceStart).replace(/^\n+/, '');
  if (!trimmed) {
    return text.slice(-MAX_SUBAGENT_STREAMING_TEXT);
  }

  return `${SUBAGENT_TRIM_PREFIX}${trimmed}`;
}

// ============================================================
// Main Types
// ============================================================

export interface ImageContentBlock {
  type: 'image';
  source: {
    type: 'base64';
    media_type: string;
    data: string;
  };
}

export interface ThinkingBlock {
  type: 'thinking';
  thinking: string;
  signature?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  isHistory?: boolean;
  images?: ImageContentBlock[];
  thinkingBlocks?: ThinkingBlock[];
  toolCalls?: ToolCallEntry[];
  attachmentPreviews?: string[];
  injectedContext?: string;
  agent?: string;
}

export interface PendingAttachment {
  id: string;
  file: File;
  mimeType: string;
  fileName: string;
  dataUrl?: string;
  base64?: string;
  sizeBytes: number;
  previewKind?: 'image' | 'audio' | 'file';
  originalSizeBytes?: number;
  wasCompressed?: boolean;
  compressionSummary?: string;
}

export interface ToolCallEntry {
  // Existing
  toolCallId: string;
  name: string;
  timestamp: number; // Local timestamp (keep for backwards compat)
  status: 'running' | 'done' | 'error';

  // New fields
  runId?: string; // Parent agent run ID
  seq?: number; // Event sequence number
  serverTs?: number; // Server timestamp from event.ts
  params?: Record<string, unknown>; // Tool input parameters
  result?: unknown; // Tool output (success case)
  partialResult?: string; // Streaming sub-agent output (Agent tool)
  parentToolCallId?: string; // Set when this tool call belongs to a subagent
  error?: { message: string; [key: string]: unknown }; // Error details
  startedAt?: number; // When status became 'running'
  completedAt?: number; // When status became 'done' or 'error'
}

export interface TodoItem {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  activeForm: string;
}

type MobileOverlay = 'none' | 'sessions' | 'activity';
export type ReconnectStatus = 'idle' | 'retrying' | 'success';
export type CurrentView = 'chat' | 'dashboard' | 'assets' | 'team';
export type AppView = CurrentView;

/**
 * Session reset animation phase:
 * - null: No reset in progress
 * - 'saving': User sent /new or /reset, waiting for new session events
 * - 'starting': First event from new session received, waiting for first assistant content
 */
export type SessionResetPhase = null | 'saving' | 'starting';

export interface ResetConfirmRequest {
  nonce: string;
  sessionKey: string;
  reason: string;
  senderId?: string;
  source?: string;
  expiresAt: number;
}

export function isCurrentHistoryRequest(
  versions: Record<string, number>,
  sessionKey: string,
  startedVersion: number,
): boolean {
  return (versions[sessionKey] ?? 0) === startedVersion;
}

interface ReconnectUiState {
  reconnectStatus: ReconnectStatus;
  reconnectAttempt: number;
  nextReconnectAt: number | null;
  reconnectMessage: string | null;
}

interface AppState extends ReconnectUiState {
  // Connection
  gatewayUrl: string;
  gatewayToken: string;
  connected: boolean;
  connecting: boolean;
  hasConnectedOnce: boolean;
  connectionError: string | null;
  lastReconnectAt: number;

  // Sessions
  activeSessionKey: string | null;
  sessions: any[];
  leftPanelCollapsed: boolean;
  leftPanelWidth: number;
  rightPanelCollapsed: boolean;
  settingsOpen: boolean;
  mobileOverlay: MobileOverlay;
  currentView: CurrentView;

  // Agents
  agents: AgentInfo[];
  selectedAgentId: string | null;
  userRole: UserRole;
  allowedAgents: string[]; // Empty = no restriction (builder or unrestricted invite)

  // Messages per session
  messages: Record<string, ChatMessage[]>;

  // History loading state per session (prevents welcome screen flash)
  historyLoading: Record<string, boolean>;
  historyInvalidationVersion: Record<string, number>;

  // Streaming state
  streamingMessage: Record<string, string | null>;

  // Per-session run state
  thinking: Record<string, boolean>;
  thinkingContent: Record<string, string | null>;
  toolCalls: Record<string, ToolCallEntry[]>;
  todosBySession: Record<string, TodoItem[]>;
  pendingInjectedContext: Record<string, string | null>;

  // Attachments
  pendingAttachments: PendingAttachment[];
  sessionImageCount: Record<string, number>;

  // View routing
  currentView: AppView;

  // Voice
  voiceMode: boolean;

  // Subagent tracking
  subagentRuns: Record<string, SubagentInfo>;

  // Chat tabs (multi-session per agent)
  chatTabs: ChatTab[];

  // Unread flag for messages arriving on a non-active session not in chatTabs (e.g. agent:main:main)
  unreadMainSession: boolean;

  // Draft input texts (persisted across view switches)
  draftTexts: Record<string, string>;

  // Session reset animation state (per session key)
  sessionResetPhase: Record<string, SessionResetPhase>;
  sessionResetTimeout: Record<string, ReturnType<typeof setTimeout> | null>;

  // Pending gateway reset confirmations
  resetConfirmQueue: ResetConfirmRequest[];

  // Per-session context fill (input + cacheRead + cacheWrite from last assistant turn)
  // This is the actual context window fill, distinct from cumulative totalTokens.
  sessionContextFill: Record<string, number>;

  // Per-session transient warnings (e.g. tool probe failure)
  warnings: Record<string, { text: string; code: string }[]>;

  // Actions
  setGatewayUrl: (url: string) => void;
  setGatewayToken: (token: string) => void;
  setConnected: (connected: boolean) => void;
  setConnecting: (connecting: boolean) => void;
  setHasConnectedOnce: (value: boolean) => void;
  setConnectionError: (error: string | null) => void;
  setReconnectState: (patch: Partial<ReconnectUiState>) => void;
  resetReconnectState: () => void;
  resetTransientUiState: () => void;
  setSessions: (sessions: any[]) => void;
  setActiveSessionKey: (key: string | null) => void;
  toggleLeftPanel: () => void;
  setLeftPanelWidth: (w: number) => void;
  toggleRightPanel: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  addMessage: (sessionKey: string, message: ChatMessage) => void;
  replaceMessages: (sessionKey: string, messages: ChatMessage[]) => void;
  setStreamingMessage: (sessionKey: string, content: string | null) => void;
  setThinking: (sessionKey: string, isThinking: boolean) => void;
  setThinkingContent: (sessionKey: string, content: string | null) => void;
  setInjectedContext: (sessionKey: string, content: string | null) => void;
  addToolCall: (sessionKey: string, entry: ToolCallEntry) => void;
  updateToolCallStatus: (
    sessionKey: string,
    toolCallId: string,
    status: ToolCallEntry['status'],
  ) => void;
  patchLastMessage: (sessionKey: string, patch: Partial<ChatMessage>) => void;
  clearRunState: (sessionKey: string) => void;
  clearToolCalls: (sessionKey: string) => void;
  setSessionTodos: (sessionKey: string, todos: TodoItem[]) => void;
  setLastHistoryMessage: (sessionKey: string, message: ChatMessage) => void;
  clearMessages: (sessionKey: string) => void;
  setHistoryLoading: (sessionKey: string, loading: boolean) => void;

  addPendingAttachment: (attachment: PendingAttachment) => void;
  removePendingAttachment: (id: string) => void;
  clearPendingAttachments: () => void;
  incrementSessionImageCount: (sessionKey: string, count: number) => void;
  setMobileOverlay: (overlay: MobileOverlay) => void;
  setCurrentView: (view: CurrentView) => void;
  setVoiceMode: (voiceMode: boolean) => void;
  setDraftText: (key: string, text: string) => void;
  setAgents: (agents: AgentInfo[]) => void;
  setSelectedAgentId: (agentId: string | null) => void;
  setUserRole: (role: UserRole) => void;
  setAllowedAgents: (agents: string[]) => void;

  clearUnreadMainSession: () => void;

  // Subagent actions
  upsertSubagent: (sessionKey: string, label?: string, autoOpenOnStreaming?: boolean) => void;
  appendSubagentStreaming: (sessionKey: string, text: string) => void;
  setSubagentStreamingText: (sessionKey: string, text: string) => void;
  markSubagentCompleted: (sessionKey: string) => void;
  markSubagentError: (sessionKey: string) => void;
  reconcileSubagents: (activeSessionKeys: string[], sessionUpdates?: Record<string, number>) => void;
  pillBarSubagents: () => SubagentInfo[];
  clearCompletedSubagents: () => void;
  acknowledgeSubagent: (sessionKey: string) => void;
  clearSubagentAutoOpen: (sessionKey: string) => void;
  startSubagentFadeOut: (sessionKey: string) => void;
  removeSubagent: (sessionKey: string) => void;

  // Chat tab actions
  addChatTab: (sessionKey: string) => void;
  removeChatTab: (tabId: string) => void;
  markChatTabUnread: (sessionKey: string) => void;
  clearChatTabUnread: (sessionKey: string) => void;

  // Session reset animation actions
  startSessionReset: (sessionKey: string) => void;
  advanceSessionResetPhase: (sessionKey: string) => void;
  clearSessionReset: (sessionKey: string) => void;
  getSessionResetPhase: (sessionKey: string) => SessionResetPhase;
  enqueueResetConfirm: (request: ResetConfirmRequest) => void;
  removeResetConfirm: (nonce: string) => void;
  clearResetConfirms: () => void;

  // Context fill action
  setSessionContextFill: (sessionKey: string, fill: number) => void;

  // Warning actions
  addWarning: (sessionKey: string, warning: { text: string; code: string }) => void;
  dismissWarning: (sessionKey: string, index: number) => void;
}

const GATEWAY_URL_KEY = 'openclaw.gatewayUrl';
const GATEWAY_TOKEN_KEY = 'openclaw.gatewayToken';
const LEFT_PANEL_KEY = 'openclaw.leftPanelCollapsed';
const LEFT_PANEL_WIDTH_KEY = 'openclaw.leftPanelWidth';
const RIGHT_PANEL_KEY = 'openclaw.rightPanelCollapsed';
const SESSION_ACTIVE_KEY = 'openclaw.activeSessionKey';
const SESSION_MESSAGES_KEY = 'openclaw.messages';
const SESSION_LIST_KEY = 'openclaw.sessions';
const SESSION_SELECTED_AGENT_KEY = 'openclaw.selectedAgentId';
const SESSION_STREAMING_MESSAGE_KEY = 'openclaw.streamingMessage';
const CHAT_TABS_KEY = 'openclaw.chatTabs';
const USER_ROLE_KEY = 'clawlet.userRole';
const ALLOWED_AGENTS_KEY = 'clawlet.allowedAgents';

function defaultGatewayUrl(): string {
  return getGatewayUrlForCurrentServer();
}

function loadStoredValue(key: string, fallback = ''): string {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const value = window.localStorage.getItem(key);
    return value ?? fallback;
  } catch {
    return fallback;
  }
}

function persistValue(key: string, value: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore persistence failures.
  }
}

function loadSessionValue<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function persistSession(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // sessionStorage full or unavailable
  }
}

function clearSessionValue(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // sessionStorage unavailable
  }
}

function loadStreamingMessage(): Record<string, string | null> {
  // Streaming UI state is ephemeral. Never restore it across reloads.
  clearSessionValue(SESSION_STREAMING_MESSAGE_KEY);
  return {};
}

function loadUserRole(): UserRole {
  if (typeof window === 'undefined') return 'builder';
  try {
    const stored = window.localStorage.getItem(USER_ROLE_KEY);
    return stored === 'member' ? 'member' : 'builder';
  } catch {
    return 'builder';
  }
}

function persistUserRole(role: UserRole): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(USER_ROLE_KEY, role);
  } catch {
    // Ignore persistence failures.
  }
}

function loadAllowedAgents(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    // Only load for members to prevent builder from inheriting a test invite's restrictions
    if (loadUserRole() !== 'member') return [];

    const raw = window.localStorage.getItem(ALLOWED_AGENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((a): a is string => typeof a === 'string') : [];
  } catch {
    return [];
  }
}

/** Extract agentId from a session key. Keys follow format: agent:<agentId>:<sessionId> */
export function agentIdFromSessionKey(key: string): string {
  if (key.startsWith('agent:')) {
    const parts = key.split(':');
    return parts[1] ?? 'main';
  }
  return 'main';
}

/** Debounced message persistence to avoid thrashing sessionStorage on every streaming token */
let messagePersistTimer: ReturnType<typeof setTimeout> | null = null;
function persistMessagesDebounced(messages: Record<string, ChatMessage[]>): void {
  if (messagePersistTimer) clearTimeout(messagePersistTimer);
  messagePersistTimer = setTimeout(() => {
    persistSession(SESSION_MESSAGES_KEY, messages);
  }, 500);
}

export const useAppStore = create<AppState>((set) => ({
  gatewayUrl: loadStoredValue(GATEWAY_URL_KEY, defaultGatewayUrl()),
  gatewayToken: loadStoredValue(GATEWAY_TOKEN_KEY, ''),
  connected: false,
  connecting: false,
  hasConnectedOnce: false,
  connectionError: null,
  reconnectStatus: 'idle',
  reconnectAttempt: 0,
  nextReconnectAt: null,
  reconnectMessage: null,
  lastReconnectAt: 0,

  activeSessionKey: loadSessionValue<string | null>(SESSION_ACTIVE_KEY, null),
  sessions: loadSessionValue<any[]>(SESSION_LIST_KEY, []),
  leftPanelCollapsed: loadStoredValue(LEFT_PANEL_KEY, 'false') === 'true',
  leftPanelWidth: (() => {
    const raw = loadStoredValue(LEFT_PANEL_WIDTH_KEY, '256');
    const n = Number(raw);
    return Number.isFinite(n) && n >= 180 && n <= 480 ? n : 256;
  })(),
  rightPanelCollapsed: loadStoredValue(RIGHT_PANEL_KEY, 'false') === 'true',
  settingsOpen: false,
  mobileOverlay: 'none' as MobileOverlay,
  currentView: 'chat' as CurrentView,
  agents: [],
  selectedAgentId: loadSessionValue<string | null>(SESSION_SELECTED_AGENT_KEY, null),
  userRole: loadUserRole(),
  allowedAgents: loadAllowedAgents(),
  messages: loadSessionValue<Record<string, ChatMessage[]>>(SESSION_MESSAGES_KEY, {}),
  historyLoading: {},
  historyInvalidationVersion: {},
  streamingMessage: loadStreamingMessage(),
  thinking: {},
  thinkingContent: {},
  toolCalls: {},
  todosBySession: {},
  pendingInjectedContext: {},
  pendingAttachments: [],
  sessionImageCount: {},
  voiceMode: false,
  subagentRuns: {},
  chatTabs: loadSessionValue<ChatTab[]>(CHAT_TABS_KEY, []),
  unreadMainSession: false,
  draftTexts: {},
  sessionResetPhase: {},
  sessionResetTimeout: {},
  resetConfirmQueue: [],
  sessionContextFill: {},
  warnings: {},

  setGatewayUrl: (url) => {
    persistValue(GATEWAY_URL_KEY, url);
    set({ gatewayUrl: url });
  },
  setGatewayToken: (token) => {
    persistValue(GATEWAY_TOKEN_KEY, token);
    set({ gatewayToken: token });
  },
  setConnected: (connected) => set({ connected }),
  setConnecting: (connecting) => set({ connecting }),
  setHasConnectedOnce: (hasConnectedOnce) => set({ hasConnectedOnce }),
  setConnectionError: (connectionError) => set({ connectionError }),
  setReconnectState: (patch) => set(patch as Partial<AppState>),
  resetReconnectState: () =>
    set({
      reconnectStatus: 'idle',
      reconnectAttempt: 0,
      nextReconnectAt: null,
      reconnectMessage: null,
    }),
  resetTransientUiState: () =>
    set({
      streamingMessage: {},
      thinking: {},
      thinkingContent: {},
      toolCalls: {},
      todosBySession: {},
      pendingInjectedContext: {},
      lastReconnectAt: Date.now(),
    }),
  setSessions: (sessions) => {
    persistSession(SESSION_LIST_KEY, sessions);
    set((state) => {
      let subagentRunsChanged = false;
      const nextSubagentRuns: Record<string, SubagentInfo> = {};

      for (const [sessionKey, info] of Object.entries(state.subagentRuns)) {
        const rawLabel = info.rawLabel ?? findSessionLabel(sessions, sessionKey);
        const label = deriveSubagentLabel(sessionKey, rawLabel);

        if (rawLabel !== info.rawLabel || label !== info.label) {
          subagentRunsChanged = true;
          nextSubagentRuns[sessionKey] = { ...info, rawLabel, label };
        } else {
          nextSubagentRuns[sessionKey] = info;
        }
      }

      clearResolvedSubagentLabelRefreshes(nextSubagentRuns);

      return subagentRunsChanged
        ? { sessions, subagentRuns: nextSubagentRuns }
        : { sessions };
    });
  },
  setActiveSessionKey: (activeSessionKey) => {
    persistSession(SESSION_ACTIVE_KEY, activeSessionKey);
    set({ activeSessionKey });
  },
  toggleLeftPanel: () =>
    set((state) => {
      const next = !state.leftPanelCollapsed;
      persistValue(LEFT_PANEL_KEY, String(next));
      return { leftPanelCollapsed: next };
    }),
  setLeftPanelWidth: (w: number) => {
    const clamped = Math.max(180, Math.min(480, w));
    persistValue(LEFT_PANEL_WIDTH_KEY, String(Math.round(clamped)));
    set({ leftPanelWidth: clamped });
  },
  toggleRightPanel: () =>
    set((state) => {
      const next = !state.rightPanelCollapsed;
      persistValue(RIGHT_PANEL_KEY, String(next));
      return { rightPanelCollapsed: next };
    }),
  openSettings: () => set({ settingsOpen: true }),
  closeSettings: () => set({ settingsOpen: false }),

  addMessage: (sessionKey, message) =>
    set((state) => {
      const existing = state.messages[sessionKey] ?? [];
      // Defensive dedup: skip if the last message has identical role + content
      const last = existing[existing.length - 1];
      if (
        last &&
        last.role === message.role &&
        last.content === message.content &&
        last.content.length > 0
      ) {
        return state;
      }
      const nextMessages = {
        ...state.messages,
        [sessionKey]: [...existing, message],
      };
      persistMessagesDebounced(nextMessages);

      // Mark chat tab unread if this session is in a non-active tab
      let nextTabs = state.chatTabs;
      let nextUnreadMainSession = state.unreadMainSession;
      if (sessionKey !== state.activeSessionKey && message.role === 'assistant') {
        const tabIdx = nextTabs.findIndex((t) => t.sessionKey === sessionKey);
        if (tabIdx !== -1 && !nextTabs[tabIdx].hasUnread) {
          nextTabs = [...nextTabs];
          nextTabs[tabIdx] = { ...nextTabs[tabIdx], hasUnread: true };
          persistSession(CHAT_TABS_KEY, nextTabs);
        } else if (tabIdx === -1) {
          // Message arrived for a session not in chatTabs — flag it so the UI can notify
          console.warn('[session-routing] message arrived for non-active session not in chatTabs', { sessionKey, activeSessionKey: state.activeSessionKey });
          nextUnreadMainSession = true;
        }
      }

      return { messages: nextMessages, chatTabs: nextTabs, unreadMainSession: nextUnreadMainSession };
    }),

  replaceMessages: (sessionKey, messages) =>
    set((state) => {
      const next = {
        ...state.messages,
        [sessionKey]: messages,
      };
      persistMessagesDebounced(next);
      return { messages: next };
    }),

  setStreamingMessage: (sessionKey, content) =>
    set((state) => ({
      streamingMessage: {
        ...state.streamingMessage,
        [sessionKey]: content,
      },
    })),

  setThinking: (sessionKey, isThinking) =>
    set((state) => ({
      thinking: {
        ...state.thinking,
        [sessionKey]: isThinking,
      },
    })),

  setThinkingContent: (sessionKey, content) =>
    set((state) => ({
      thinkingContent: {
        ...state.thinkingContent,
        [sessionKey]: content,
      },
    })),

  setInjectedContext: (sessionKey, content) =>
    set((state) => ({
      pendingInjectedContext: {
        ...state.pendingInjectedContext,
        [sessionKey]: content,
      },
    })),

  addToolCall: (sessionKey, entry) =>
    set((state) => {
      const existing = state.toolCalls[sessionKey] ?? [];
      const index = existing.findIndex((item) => item.toolCallId === entry.toolCallId);

      if (index >= 0) {
        const next = [...existing];
        next[index] = { ...next[index], ...entry };
        return {
          toolCalls: {
            ...state.toolCalls,
            [sessionKey]: next,
          },
        };
      }

      return {
        toolCalls: {
          ...state.toolCalls,
          [sessionKey]: [...existing, entry],
        },
      };
    }),

  updateToolCallStatus: (sessionKey, toolCallId, status) =>
    set((state) => ({
      toolCalls: {
        ...state.toolCalls,
        [sessionKey]: (state.toolCalls[sessionKey] ?? []).map((entry) =>
          entry.toolCallId === toolCallId ? { ...entry, status } : entry,
        ),
      },
    })),

  patchLastMessage: (sessionKey, patch) =>
    set((state) => {
      const existing = state.messages[sessionKey] ?? [];
      if (existing.length === 0) return state;

      const updated = [...existing];
      updated[updated.length - 1] = { ...updated[updated.length - 1], ...patch };
      const next = { ...state.messages, [sessionKey]: updated };
      persistMessagesDebounced(next);
      return { messages: next };
    }),

  clearRunState: (sessionKey) =>
    set((state) => ({
      streamingMessage: {
        ...state.streamingMessage,
        [sessionKey]: null,
      },
      thinking: {
        ...state.thinking,
        [sessionKey]: false,
      },
      thinkingContent: {
        ...state.thinkingContent,
        [sessionKey]: null,
      },
      pendingInjectedContext: {
        ...state.pendingInjectedContext,
        [sessionKey]: null,
      },
      toolCalls: {
        ...state.toolCalls,
        [sessionKey]: [],
      },
      todosBySession: {
        ...state.todosBySession,
        [sessionKey]: [],
      },
    })),

  clearToolCalls: (sessionKey) =>
    set((state) => ({
      toolCalls: {
        ...state.toolCalls,
        [sessionKey]: [],
      },
    })),

  setSessionTodos: (sessionKey, todos) =>
    set((state) => ({
      todosBySession: {
        ...state.todosBySession,
        [sessionKey]: todos,
      },
    })),


  setLastHistoryMessage: (sessionKey, message) =>
    set((state) => {
      const existing = state.messages[sessionKey] ?? [];
      const withoutHistory = existing.filter((item) => !item.isHistory);

      const next = {
        ...state.messages,
        [sessionKey]: [{ ...message, isHistory: true }, ...withoutHistory],
      };
      persistMessagesDebounced(next);
      return { messages: next };
    }),

  clearMessages: (sessionKey) =>
    set((state) => {
      const next = {
        ...state.messages,
        [sessionKey]: [],
      };
      persistMessagesDebounced(next);
      const currentHistoryVersion = state.historyInvalidationVersion[sessionKey] ?? 0;
      return {
        messages: next,
        historyInvalidationVersion: {
          ...state.historyInvalidationVersion,
          [sessionKey]: currentHistoryVersion + 1,
        },
        streamingMessage: {
          ...state.streamingMessage,
          [sessionKey]: null,
        },
        thinking: {
          ...state.thinking,
          [sessionKey]: false,
        },
        thinkingContent: {
          ...state.thinkingContent,
          [sessionKey]: null,
        },
        toolCalls: {
          ...state.toolCalls,
          [sessionKey]: [],
        },
        historyLoading: {
          ...state.historyLoading,
          [sessionKey]: false,
        },
      };
    }),

  setHistoryLoading: (sessionKey, loading) =>
    set((state) => ({
      historyLoading: {
        ...state.historyLoading,
        [sessionKey]: loading,
      },
    })),

  addPendingAttachment: (attachment) =>
    set((state) => ({
      pendingAttachments: [...state.pendingAttachments, attachment],
    })),

  removePendingAttachment: (id) =>
    set((state) => ({
      pendingAttachments: state.pendingAttachments.filter((attachment) => attachment.id !== id),
    })),

  clearPendingAttachments: () => set({ pendingAttachments: [] }),

  incrementSessionImageCount: (sessionKey, count) =>
    set((state) => ({
      sessionImageCount: {
        ...state.sessionImageCount,
        [sessionKey]: (state.sessionImageCount[sessionKey] ?? 0) + count,
      },
    })),

  setMobileOverlay: (mobileOverlay) => set({ mobileOverlay }),
  setCurrentView: (currentView) => set({ currentView }),
  setVoiceMode: (voiceMode) => set({ voiceMode }),
  setDraftText: (key, text) =>
    set((state) => {
      if (!text) {
        if (!(key in state.draftTexts)) return state;
        const next = { ...state.draftTexts };
        delete next[key];
        return { draftTexts: next };
      }
      return { draftTexts: { ...state.draftTexts, [key]: text } };
    }),
  setAgents: (agents) => set({ agents }),
  setSelectedAgentId: (selectedAgentId) => {
    persistSession(SESSION_SELECTED_AGENT_KEY, selectedAgentId);
    set({ selectedAgentId });
  },
  setUserRole: (role) => {
    persistUserRole(role);
    set({
      userRole: role,
      allowedAgents: role === 'member' ? loadAllowedAgents() : [],
    });
  },
  setAllowedAgents: (allowedAgents) => {
    try {
      localStorage.setItem(ALLOWED_AGENTS_KEY, JSON.stringify(allowedAgents));
    } catch {
      // localStorage not available
    }
    set({ allowedAgents });
  },

  // ============================================================
  // Subagent Actions
  // ============================================================

  upsertSubagent: (sessionKey, label, autoOpenOnStreaming = false) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      const now = Date.now();
      const rawLabel = label?.trim() || existing?.rawLabel || findSessionLabel(state.sessions, sessionKey);
      const derivedLabel = deriveSubagentLabel(sessionKey, rawLabel);
      const needsLabelRefresh = isDefaultDerivedSubagentLabel(sessionKey, rawLabel, derivedLabel);

      if (needsLabelRefresh) {
        scheduleSubagentLabelRefresh(sessionKey);
      } else {
        attemptedSubagentLabelRefreshes.delete(sessionKey);
      }

      if (existing) {
        // Update existing entry
        return {
          subagentRuns: {
            ...state.subagentRuns,
            [sessionKey]: {
              ...existing,
              label: derivedLabel,
              rawLabel,
              lastActivityTime: now,
              hasReceivedLiveEvent: true,
              autoOpenOnStreaming: existing.autoOpenOnStreaming || autoOpenOnStreaming,
            },
          },
        };
      }

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            sessionKey,
            parentSessionKey: state.activeSessionKey ?? undefined,
            label: derivedLabel,
            rawLabel,
            status: 'running',
            streamingText: '',
            startTime: now,
            lastActivityTime: now,
            hasReceivedLiveEvent: true,
            completedAt: null,
            acknowledged: false,
            fadingOut: false,
            autoOpenOnStreaming,
          },
        },
      };
    }),

  appendSubagentStreaming: (sessionKey, text) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      if (!existing) return state;

      // Append text, cap at MAX_SUBAGENT_STREAMING_TEXT using line-aware front trimming
      const newText = trimSubagentStreamingText(existing.streamingText + text);

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            ...existing,
            streamingText: newText,
            lastActivityTime: Date.now(),
            hasReceivedLiveEvent: true,
          },
        },
      };
    }),

  setSubagentStreamingText: (sessionKey, text) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      if (!existing) return state;

      const nextText = trimSubagentStreamingText(text);

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            ...existing,
            streamingText: nextText,
            lastActivityTime: Date.now(),
            hasReceivedLiveEvent: true,
          },
        },
      };
    }),

  markSubagentCompleted: (sessionKey) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      if (!existing || existing.status !== 'running') return state;

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            ...existing,
            status: 'completed',
            completedAt: Date.now(),
          },
        },
      };
    }),

  markSubagentError: (sessionKey) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      if (!existing || existing.status !== 'running') return state;

      // If the subagent has produced output, it likely completed successfully
      // but the gateway sent a cleanup error on main session reset.
      // Treat this as completion to avoid false-positive red pills.
      // See: bf-badge-pill-v2 spec — gateway sends lifecycle:error for child
      // sessions when main session resets, even if the child completed successfully.
      const hasProducedOutput = existing.streamingText.trim().length > 0;

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            ...existing,
            status: hasProducedOutput ? 'completed' : 'error',
            completedAt: Date.now(),
          },
        },
      };
    }),

  reconcileSubagents: (activeSessionKeys, sessionUpdates) =>
    set((state) => {
      const now = Date.now();
      const STALE_THRESHOLD_MS = 30 * 1000; // 30 seconds
      let changed = false;
      const next = { ...state.subagentRuns };

      for (const [key, info] of Object.entries(next)) {
        if (info.status !== 'running') continue;

        // If the subagent key is NOT in the active session list, mark it completed
        if (!activeSessionKeys.includes(key)) {
          next[key] = { ...info, status: 'completed', completedAt: now };
          changed = true;
          continue;
        }

        // If we haven't received any live events AND the session is stale, mark completed
        if (!info.hasReceivedLiveEvent && sessionUpdates) {
          const updatedAt = sessionUpdates[key];
          if (updatedAt && now - updatedAt > STALE_THRESHOLD_MS) {
            next[key] = { ...info, status: 'completed', completedAt: now };
            changed = true;
          }
        }
      }

      return changed ? { subagentRuns: next } : state;
    }),

  pillBarSubagents: (): SubagentInfo[] => {
    // Use `get` parameter from zustand to avoid circular reference
    // This function is called from components, so we access state via zustand's get
    const { activeSessionKey, subagentRuns } = useAppStore.getState();

    return (Object.values(subagentRuns) as SubagentInfo[])
      .filter((info: SubagentInfo) => {
        if (info.parentSessionKey && info.parentSessionKey !== activeSessionKey) return false;
        // Always show running subagents
        if (info.status === 'running') return true;
        // Show during fade-out animation (any status)
        if (info.fadingOut) return true;
        // Completed: show until user has acknowledged (opened popup)
        if (info.status === 'completed' && !info.acknowledged) return true;
        // Error: always show until explicitly dismissed
        if (info.status === 'error') return true;
        return false;
      })
      .sort((a: SubagentInfo, b: SubagentInfo) => a.startTime - b.startTime);
  },

  clearCompletedSubagents: () =>
    set((state) => {
      const next: Record<string, SubagentInfo> = {};
      for (const [key, info] of Object.entries(state.subagentRuns)) {
        if (info.status === 'running') {
          next[key] = info;
        }
      }
      return { subagentRuns: next };
    }),

  acknowledgeSubagent: (sessionKey) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      if (!existing) return state;

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            ...existing,
            acknowledged: true,
          },
        },
      };
    }),

  clearSubagentAutoOpen: (sessionKey) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      if (!existing || !existing.autoOpenOnStreaming) return state;

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            ...existing,
            autoOpenOnStreaming: false,
          },
        },
      };
    }),

  startSubagentFadeOut: (sessionKey) =>
    set((state) => {
      const existing = state.subagentRuns[sessionKey];
      if (!existing) return state;

      return {
        subagentRuns: {
          ...state.subagentRuns,
          [sessionKey]: {
            ...existing,
            fadingOut: true,
          },
        },
      };
    }),

  removeSubagent: (sessionKey) =>
    set((state) => {
      attemptedSubagentLabelRefreshes.delete(sessionKey);
      const next = { ...state.subagentRuns };
      delete next[sessionKey];
      return { subagentRuns: next };
    }),

  // ============================================================
  // Chat Tab Actions
  // ============================================================

  addChatTab: (sessionKey) =>
    set((state) => {
      const id = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const next = [...state.chatTabs, { id, sessionKey, hasUnread: false }];
      persistSession(CHAT_TABS_KEY, next);
      return { chatTabs: next };
    }),

  removeChatTab: (tabId) =>
    set((state) => {
      const next = state.chatTabs.filter((t) => t.id !== tabId);
      persistSession(CHAT_TABS_KEY, next);
      return { chatTabs: next };
    }),

  markChatTabUnread: (sessionKey) =>
    set((state) => {
      if (state.activeSessionKey === sessionKey) return state;
      const idx = state.chatTabs.findIndex((t) => t.sessionKey === sessionKey);
      if (idx === -1 || state.chatTabs[idx].hasUnread) return state;
      const next = [...state.chatTabs];
      next[idx] = { ...next[idx], hasUnread: true };
      persistSession(CHAT_TABS_KEY, next);
      return { chatTabs: next };
    }),

  clearChatTabUnread: (sessionKey) =>
    set((state) => {
      const idx = state.chatTabs.findIndex((t) => t.sessionKey === sessionKey);
      if (idx === -1 || !state.chatTabs[idx].hasUnread) return state;
      const next = [...state.chatTabs];
      next[idx] = { ...next[idx], hasUnread: false };
      persistSession(CHAT_TABS_KEY, next);
      return { chatTabs: next };
    }),

  clearUnreadMainSession: () => set({ unreadMainSession: false }),

  // ============================================================
  // Session Reset Animation Actions
  // ============================================================

  startSessionReset: (sessionKey) =>
    set((state) => {
      // Clear any existing timeout for this session
      const existingTimeout = state.sessionResetTimeout[sessionKey];
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set 60s safety timeout
      const timeoutId = setTimeout(() => {
        useAppStore.getState().clearSessionReset(sessionKey);
      }, 60_000);

      return {
        sessionResetPhase: {
          ...state.sessionResetPhase,
          [sessionKey]: 'saving' as const,
        },
        sessionResetTimeout: {
          ...state.sessionResetTimeout,
          [sessionKey]: timeoutId,
        },
      };
    }),

  advanceSessionResetPhase: (sessionKey) =>
    set((state) => {
      const currentPhase = state.sessionResetPhase[sessionKey];
      if (currentPhase !== 'saving') return state;

      return {
        sessionResetPhase: {
          ...state.sessionResetPhase,
          [sessionKey]: 'starting' as const,
        },
      };
    }),

  clearSessionReset: (sessionKey) =>
    set((state) => {
      // Clear the timeout if it exists
      const existingTimeout = state.sessionResetTimeout[sessionKey];
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const nextPhase = { ...state.sessionResetPhase };
      delete nextPhase[sessionKey];

      const nextTimeout = { ...state.sessionResetTimeout };
      delete nextTimeout[sessionKey];

      return {
        sessionResetPhase: nextPhase,
        sessionResetTimeout: nextTimeout,
      };
    }),

  getSessionResetPhase: (sessionKey) => {
    return useAppStore.getState().sessionResetPhase[sessionKey] ?? null;
  },

  enqueueResetConfirm: (request) =>
    set((state) => {
      const existingIndex = state.resetConfirmQueue.findIndex((entry) => entry.nonce === request.nonce);
      if (existingIndex !== -1) {
        const next = [...state.resetConfirmQueue];
        next[existingIndex] = request;
        return { resetConfirmQueue: next };
      }

      return {
        resetConfirmQueue: [...state.resetConfirmQueue, request],
      };
    }),

  removeResetConfirm: (nonce) =>
    set((state) => ({
      resetConfirmQueue: state.resetConfirmQueue.filter((entry) => entry.nonce !== nonce),
    })),

  clearResetConfirms: () => set({ resetConfirmQueue: [] }),

  setSessionContextFill: (sessionKey, fill) =>
    set((state) => ({
      sessionContextFill: {
        ...state.sessionContextFill,
        [sessionKey]: fill,
      },
    })),

  addWarning: (sessionKey, warning) =>
    set((state) => ({
      warnings: {
        ...state.warnings,
        [sessionKey]: [...(state.warnings[sessionKey] || []), warning],
      },
    })),

  dismissWarning: (sessionKey, index) =>
    set((state) => {
      const current = state.warnings[sessionKey] || [];
      return {
        warnings: {
          ...state.warnings,
          [sessionKey]: current.filter((_, i) => i !== index),
        },
      };
    }),
}));
